import * as React from 'react'
import { CreditCard, Loader2, Undo2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCancelSubscription, useCheckoutReturn, useOpenBillingPortal, useStartCheckout } from '@/hooks/useBilling'
import { billingDateLabel } from '@/lib/billing'
import type { BillingSummaryDto } from '@lib/dto/billing-summary.dto'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import { AcceptLegalStep, FirstVenueStep } from './OwnerTasks'

/** Which actions a company in this state has. Exported for its test. */
export function actionsFor(billing: BillingSummaryDto): {
  subscribe: string | null
  portal: boolean
  cancel: boolean
  resume: boolean
} {
  const { state } = billing
  let subscribe: string | null = null
  if (state === 'signup_incomplete') subscribe = 'Continue to payment'
  else if (state === 'comp_ending' || state === 'trial_cancelled') subscribe = 'Subscribe'
  else if (billing.state === 'locked' && (billing.lockReason === 'cancelled' || billing.lockReason === 'comp_ended')) {
    subscribe = 'Subscribe again'
  }
  const paying = state === 'trialing' || state === 'active' || state === 'past_due' || state === 'cancelling'
  return {
    subscribe,
    portal: paying || (billing.state === 'locked' && billing.lockReason === 'non_payment'),
    // Not while a payment has failed: that is paid first, then canceled.
    cancel: state === 'trialing' || state === 'active',
    resume: state === 'cancelling',
  }
}

/** Confirm canceling — the consequences differ so much between a trial and a paid plan. */
function CancelDialog({
  billing,
  open,
  onOpenChange,
}: {
  billing: BillingSummaryDto
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const cancel = useCancelSubscription()
  const [error, setError] = React.useState<string | null>(null)
  const trial = billing.state === 'trialing'

  function confirm(): void {
    setError(null)
    cancel.mutate('cancel', { onSuccess: () => onOpenChange(false), onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{trial ? 'Cancel the trial?' : 'Cancel the subscription?'}</DialogTitle>
          <DialogDescription>
            {trial
              ? 'Nothing will be charged. Everyone loses access now, and the organization and everything in it is deleted in 30 days unless you subscribe before then.'
              : `Everyone keeps full access until ${billing.currentPeriodEndsAt ? billingDateLabel(billing.currentPeriodEndsAt) : 'the end of the period'}, then the organization is locked. You can undo this until then.`}
          </DialogDescription>
        </DialogHeader>
        {error ? <FormError>{error}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Keep it
          </Button>
          <Button pending={cancel.isPending} type="button" variant="destructive" onClick={confirm}>
            {trial ? 'Cancel trial' : 'Cancel subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The billing owner's buttons: subscribe, manage the card and invoices, cancel,
 * or undo a cancellation — whichever this state allows. Shown on the billing
 * page and on the lock screen, since while locked that is where the owner is.
 *
 * Before subscribing, any step the owner has not done comes first: the first
 * venue of a company created by hand, then accepting the terms.
 */
export function BillingActions({ organization }: { organization: OrganizationDto }) {
  const { billing, ownerTasks } = organization
  const actions = actionsFor(billing)
  const checkout = useStartCheckout()
  const portal = useOpenBillingPortal()
  const resume = useCancelSubscription()
  const returning = useCheckoutReturn()
  const [canceling, setCanceling] = React.useState(false)
  const error = checkout.error ?? portal.error ?? resume.error

  if (returning.finishing) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Finishing your subscription…
      </p>
    )
  }

  if (actions.subscribe && ownerTasks?.createVenue) return <FirstVenueStep />
  if (actions.subscribe && ownerTasks?.acceptLegal) return <AcceptLegalStep />

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {actions.subscribe ? (
          <Button pending={checkout.isPending} type="button" onClick={() => checkout.mutate()}>
            <CreditCard className="h-4 w-4" />
            {actions.subscribe}
          </Button>
        ) : null}
        {actions.portal ? (
          <Button
            type="button"
            variant={actions.subscribe ? 'outline' : 'default'}
            onClick={() => portal.mutate()}
            pending={portal.isPending}
          >
            <CreditCard className="h-4 w-4" />
            {(billing.state === 'locked' && billing.lockReason === 'non_payment') || billing.state === 'past_due'
              ? 'Update card and pay'
              : 'Manage billing'}
          </Button>
        ) : null}
        {actions.resume ? (
          <Button type="button" variant="outline" onClick={() => resume.mutate('resume')} disabled={resume.isPending}>
            <Undo2 className="h-4 w-4" /> Undo cancellation
          </Button>
        ) : null}
        {actions.cancel ? (
          <Button type="button" variant="ghost" onClick={() => setCanceling(true)}>
            <XCircle className="h-4 w-4" /> Cancel
          </Button>
        ) : null}
      </div>
      {returning.error ? <FormError>{returning.error}</FormError> : null}
      {error ? <FormError>{requestErrorMessage(error)}</FormError> : null}
      {actions.cancel ? <CancelDialog billing={billing} open={canceling} onOpenChange={setCanceling} /> : null}
    </div>
  )
}
